/**
 * Chain resolution before a wallet operator starts (W3-11).
 *
 * The node's own chain wins. Otherwise the address format decides when it is
 * unambiguous (TRX, BTC, SOL, …). Only an ambiguous format (an EVM hex address
 * is valid on many chains) asks the backend: GET /chains/detect, which probes
 * the EVM RPCs through the existing chain probe service.
 */
import { detectChainCandidates } from '../utils/chainDetect';

const API_BASE_URL = (import.meta.env as unknown as Record<string, string>).VITE_API_URL || 'http://localhost:8000/api/v1';

export interface ChainDetectResult {
  value: string;
  candidates: string[];
  ambiguous: boolean;
  recognised: boolean;
  chosen_chain: string | null;
  chosen_reason: string | null;
  normalized: string | null;
  probed: boolean;
}

/** GET /chains/detect?value=… (gated `workbench.triage`). */
export async function detectChainRemote(value: string, probe = true): Promise<ChainDetectResult> {
  const token = localStorage.getItem('token') || '';
  const q = new URLSearchParams({ value, probe: probe ? 'true' : 'false' });
  const res = await fetch(`${API_BASE_URL}/chains/detect?${q.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`chain detection failed (${res.status})`);
  return res.json();
}

export type ChainResolution =
  | { ok: true; chain: string; source: 'node' | 'format' | 'probe' }
  | { ok: false; reason: string };

/** The chain to run a wallet operator on, or why it cannot be decided. */
export async function resolveWalletChain(
  address: string,
  nodeChain?: string | null,
  detect: (value: string) => Promise<ChainDetectResult> = detectChainRemote,
): Promise<ChainResolution> {
  const own = String(nodeChain || '').trim().toUpperCase();
  if (own && own !== 'UNKNOWN') return { ok: true, chain: own, source: 'node' };
  const local = detectChainCandidates(address);
  if (local.length === 1) return { ok: true, chain: local[0], source: 'format' };
  try {
    const r = await detect(address);
    if (r.chosen_chain) return { ok: true, chain: r.chosen_chain, source: r.probed ? 'probe' : 'format' };
    if (!r.recognised) return { ok: false, reason: 'the address format is not recognised on any supported chain' };
    return { ok: false, reason: `the chain is ambiguous (${r.candidates.slice(0, 4).join(', ')}…); set the chain on the node` };
  } catch (e) {
    return { ok: false, reason: e instanceof Error ? e.message : 'chain detection failed' };
  }
}
